import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Link,
  IconButton,
  Button,
  Typography,
} from '@mui/material';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import LocationSearchingIcon from '@mui/icons-material/LocationSearching';
import { useSelector } from 'react-redux';
import { formatHours, formatSpeed, formatTime } from '../common/util/formatter';
import ReportFilter from './components/ReportFilter';
import { prefixString } from '../common/util/stringUtils';
import { useTranslation } from '../common/components/LocalizationProvider';
import PageLayout from '../common/components/PageLayout';
import ReportsMenu from './components/ReportsMenu';
import { useCatch, useEffectAsync } from '../reactHelper';
import useReportStyles from './common/useReportStyles';
import TableShimmer from '../common/components/TableShimmer';
import { useAttributePreference, usePreference } from '../common/util/preferences';
import MapView from '../map/core/MapView';
import MapGeofence from '../map/MapGeofence';
import MapPositions from '../map/MapPositions';
import MapCamera from '../map/MapCamera';
import PositionModal from '../other/PositionModal';
import MapAccuracy from '../map/main/MapAccuracy';

const MobilityReportPage = () => {
  const navigate = useNavigate();
  const classes = useReportStyles();
  const t = useTranslation();

  const devices = useSelector((state) => state.devices.items);
  const geofences = useSelector((state) => state.geofences.items);

  const speedUnit = useAttributePreference('speedUnit');
  const hours12 = usePreference('twelveHourFormat');

  const [columns] = useState(['deviceName', 'lastParkingTime', 'duration', 'immobile', 'inHomeGeofence', 'geofenceId']);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [position, setPosition] = useState(null);
  const [positionModalOpen, setPositionModalOpen] = useState(false);

  const [mobilityGeofence, setMobilityGeofence] = useState('all');
  const [mobilitySubjects, setMobilitySubjects] = useState('immobile');

  const handlePositionModalClose = () => {
    setPositionModalOpen(false);
  };

  const onMarkerClick = useCallback((positionId) => {
    setPositionModalOpen(!!positionId);
  }, [setPositionModalOpen]);

  useEffectAsync(async () => {
    if (selectedItem) {
      const response = await fetch(`/api/positions?id=${selectedItem.positionId}`);
      if (response.ok) {
        const positions = await response.json();
        if (positions.length > 0) {
          setPosition(positions[0]);
        }
      } else {
        throw Error(await response.text());
      }
    } else {
      setPosition(null);
    }
  }, [selectedItem]);

  const handleSubmit = useCatch(async ({ type }) => {
    const query = new URLSearchParams({ geofence: mobilityGeofence, subjects: mobilitySubjects });

    if (type === 'export') {
      window.location.assign(`/api/reports/events/xlsx?${query.toString()}`);
    } else if (type === 'mail') {
      const response = await fetch(`/api/reports/events/mail?${query.toString()}`);

      if (!response.ok) {
        throw Error(await response.text());
      }
    } else {
      setLoading(true);

      try {
        const response = await fetch(`/api/reports/mobility?${query.toString()}`, {
          headers: { Accept: 'application/json' },
        });

        if (response.ok) {
          setItems(await response.json());
        } else {
          throw Error(await response.text());
        }
      } finally {
        setLoading(false);
      }
    }
  });

  const formatValue = (item, key) => {
    try {
      switch (key) {
        case 'deviceName':
          return devices?.[item?.deviceId ?? '']?.name ?? '';
        case 'lastParkingTime':
          return formatTime(item[key], 'seconds', hours12);
        case 'type':
          return t(prefixString('event', item[key]));
        case 'geofenceId':
          if (item[key] > 0) {
            const geofence = geofences[item[key]];
            return geofence && geofence.name;
          }
          return null;
        case 'immobile':
          return item[key] ? 'Ne' : 'Taip';
        case 'inHomeGeofence':
          return item[key] ? 'Taip' : 'Ne';
        case 'duration':
          return formatHours(new Date() - new Date(item.lastParkingTime));
        case 'maintenanceId':
          return item[key] > 0 ? item[key] > 0 : null;
        case 'attributes':
          switch (item.type) {
            case 'alarm':
              return t(prefixString('alarm', item.attributes.alarm));
            case 'deviceOverspeed':
              return formatSpeed(item.attributes.speed, speedUnit, t);
            case 'driverChanged':
              return item.attributes.driverUniqueId;
            case 'media':
              return (<Link href={`/api/media/${devices[item.deviceId]?.uniqueId}/${item.attributes.file}`} target="_blank">{item.attributes.file}</Link>);
            case 'commandResult':
              return item.attributes.result;
            default:
              return '';
          }
        default:
          return item[key];
      }
    } catch {
      return '';
    }
  };

  return (
    <PageLayout menu={<ReportsMenu />} breadcrumbs={['reportTitle', 'reportEvents']}>
      <div className={classes.container}>
        {selectedItem && (
          <div className={classes.containerMap}>
            <MapView>
              <MapGeofence />
              {position && (
                <>
                  <MapPositions overrideDirection showStatus={false} positions={[position]} onClick={onMarkerClick} titleField="fixTime" />
                  <MapAccuracy displayForAllPositions positions={[position]} />
                </>
              )}
            </MapView>
            {position && <MapCamera latitude={position.latitude} longitude={position.longitude} />}
          </div>
        )}
        <div className={classes.containerMain}>
          <div className={classes.header}>
            <ReportFilter showOnly ignoreDevice ignorePeriod handleSubmit={handleSubmit} handleSchedule={() => {}} multiDevice>
              <div className={classes.filterItem}>
                <FormControl fullWidth>
                  <InputLabel>{t('deviceTitle')}</InputLabel>
                  <Select
                    label={t('deviceTitle')}
                    value={mobilitySubjects}
                    onChange={(e) => setMobilitySubjects(e.target.value)}
                    MenuProps={{
                      style: {
                        maxHeight: 300,
                      },
                    }}
                  >
                    <MenuItem value="immobile">{t('reportMobilityImmobileSubjects')}</MenuItem>
                    <MenuItem value="mobile">{t('reportMobilityMobileSubjects')}</MenuItem>
                    <MenuItem value="all">{t('reportMobilityAllSubjects')}</MenuItem>
                  </Select>
                </FormControl>
              </div>
              <div className={classes.filterItem}>
                <FormControl fullWidth>
                  <InputLabel>{t('sharedGeofences')}</InputLabel>
                  <Select
                    label={t('sharedGeofences')}
                    value={mobilityGeofence}
                    onChange={(e) => setMobilityGeofence(e.target.value)}
                    MenuProps={{
                      style: {
                        maxHeight: 300,
                      },
                    }}
                  >
                    <MenuItem value="all">{t('reportMobilityGeofenceAll')}</MenuItem>
                    <MenuItem value="home">{t('reportMobilityGeofenceHome')}</MenuItem>
                    <MenuItem value="away">{t('reportMobilityGeofenceAway')}</MenuItem>
                  </Select>
                </FormControl>
              </div>
              <div className={classes.filterItem}>
                <Button
                  fullWidth
                  variant="outlined"
                  color="inherit"
                  style={{
                    color: 'gray',
                  }}
                  onClick={() => {
                    navigate('/reports/mobility/settings');
                  }}
                >
                  <Typography variant="button" noWrap>{t('settingsTitle')}</Typography>
                </Button>
              </div>
            </ReportFilter>
          </div>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell className={classes.columnAction} />
                <TableCell>Subjektas</TableCell>
                <TableCell>Paskutinio sustojimo laikas</TableCell>
                <TableCell>Stovėjimo laikas</TableCell>
                <TableCell>Judrus</TableCell>
                <TableCell>Įmonės georiboje</TableCell>
                <TableCell>Georiba</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading ? items.map((item) => (
                <TableRow key={item.deviceId}>
                  <TableCell className={classes.columnAction} padding="none">
                    {item.positionId ? selectedItem === item ? (
                      <IconButton size="small" onClick={() => setSelectedItem(null)}>
                        <GpsFixedIcon fontSize="small" />
                      </IconButton>
                    ) : (
                      <IconButton size="small" onClick={() => setSelectedItem(item)}>
                        <LocationSearchingIcon fontSize="small" />
                      </IconButton>
                    ) : ''}
                  </TableCell>
                  {columns.map((key) => (
                    <TableCell key={key}>
                      {formatValue(item, key)}
                    </TableCell>
                  ))}
                </TableRow>
              )) : (<TableShimmer columns={columns.length + 1} />)}
            </TableBody>
          </Table>
        </div>
      </div>
      <PositionModal
        open={positionModalOpen}
        handleClose={handlePositionModalClose}
        position={position}
      />
    </PageLayout>
  );
};

export default MobilityReportPage;
